/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module link/ui/linkformview
 */
import {ButtonView, FocusCycler, LabeledFieldView, SwitchButtonView, View, ViewCollection, createLabeledInputText, submitHandler, Dialog} from 'ckeditor5/src/ui.js';
import {FocusTracker, KeystrokeHandler} from 'ckeditor5/src/utils.js';
import {icons} from 'ckeditor5/src/core.js';
import '@ckeditor/ckeditor5-ui/theme/components/responsive-form/responsiveform.css';
import '../../theme/linkform.css';
import axios from 'axios';

/**
 * The link form view controller class.
 *
 * See {@link module:link/ui/linkformview~LinkFormView}.
 */
export default class LinkFormView extends View {
  /**
   * Creates an instance of the {@link module:link/ui/linkformview~LinkFormView} class.
   *
   * Also see {@link #render}.
   *
   * @param locale The localization services instance.
   * @param linkCommand Reference to {@link module:link/linkcommand~LinkCommand}.
   * @param validators  Form validators used by {@link #isValid}.
   */
  constructor(editor, locale, linkCommand, validators) {
    super(locale);
    /**
     * Tracks information about DOM focus in the form.
     */
    this.focusTracker = new FocusTracker();
    /**
     * An instance of the {@link module:utils/keystrokehandler~KeystrokeHandler}.
     */
    this.keystrokes = new KeystrokeHandler();
    /**
     * A collection of views that can be focused in the form.
     */
    this._focusables = new ViewCollection();
    const t = locale.t;
    this._validators = validators;
    this.urlInputView = this._createUrlInput();
    // CUSTOM
    this.linkT3PageButtonView = this._createT3LinkButton(editor, locale);
    this.saveButtonView = this._createButton(t('Save'), icons.check, 'ck-button-save');
    this.saveButtonView.type = 'submit';
    this.cancelButtonView = this._createButton(t('Cancel'), icons.cancel, 'ck-button-cancel', 'cancel');
    this._manualDecoratorSwitches = this._createManualDecoratorSwitches(linkCommand);
    this.children = this._createFormChildren(linkCommand.manualDecorators);
    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        // Navigate form fields backwards using the Shift + Tab keystroke.
        focusPrevious: 'shift + tab',
        // Navigate form fields forwards using the Tab key.
        focusNext: 'tab',
      },
    });
    const classList = ['ck', 'ck-link-form', 'ck-responsive-form'];
    if (linkCommand.manualDecorators.length) {
      classList.push('ck-link-form_layout-vertical', 'ck-vertical-form');
    }
    this.setTemplate({
      tag: 'form',
      attributes: {
        class: classList,
        // https://github.com/ckeditor/ckeditor5-link/issues/90
        tabindex: '-1',
      },
      children: this.children,
    });
  }

  /**
   * Obtains the state of the {@link module:ui/button/switchbuttonview~SwitchButtonView switch buttons} representing
   * {@link module:link/linkcommand~LinkCommand#manualDecorators manual link decorators}
   * in the {@link module:link/ui/linkformview~LinkFormView}.
   *
   * @returns Key-value pairs, where the key is the name of the decorator and the value is its state.
   */
  getDecoratorSwitchesState() {
    return Array.from(this._manualDecoratorSwitches).reduce((accumulator, switchButton) => {
      accumulator[switchButton.name] = switchButton.isOn;
      return accumulator;
    }, {});
  }

  /**
   * @inheritDoc
   */
  render() {
    super.render();
    submitHandler({
      view: this,
    });
    const childViews = [
      this.urlInputView,
      this.linkT3PageButtonView,
      ...this._manualDecoratorSwitches,
      this.saveButtonView,
      this.cancelButtonView,
    ];
    childViews.forEach(v => {
      // Register the view as focusable.
      this._focusables.add(v);
      // Register the view in the focus tracker.
      this.focusTracker.add(v.element);
    });
    // Start listening for the keystrokes coming from #element.
    this.keystrokes.listenTo(this.element);
  }

  /**
   * @inheritDoc
   */
  destroy() {
    super.destroy();
    this.focusTracker.destroy();
    this.keystrokes.destroy();
  }

  /**
   * Focuses the fist {@link #_focusables} in the form.
   */
  focus() {
    this._focusCycler.focusFirst();
  }

  /**
   * Validates the form and returns `false` when some fields are invalid.
   */
  isValid() {
    this.resetFormStatus();
    for (const validator of this._validators) {
      const errorText = validator(this);
      // One error per field is enough.
      if (errorText) {
        // Apply updated error.
        this.urlInputView.errorText = errorText;
        return false;
      }
    }
    return true;
  }

  /**
   * Cleans up the supplementary error and information text of the {@link #urlInputView}
   * bringing them back to the state when the form has been displayed for the first time.
   *
   * See {@link #isValid}.
   */
  resetFormStatus() {
    this.urlInputView.errorText = null;
  }

  /**
   * Creates a labeled input view.
   *
   * @returns Labeled field view instance.
   */
  _createUrlInput() {
    const t = this.locale.t;
    const labeledInput = new LabeledFieldView(this.locale, createLabeledInputText);
    labeledInput.label = t('Link URL');
    return labeledInput;
  }

  /**
   * Creates a button view.
   *
   * @param label The button label.
   * @param icon The button icon.
   * @param className The additional button CSS class name.
   * @param eventName An event name that the `ButtonView#execute` event will be delegated to.
   * @returns The button view instance.
   */
  _createButton(label, icon, className, eventName) {
    const button = new ButtonView(this.locale);
    button.set({
      label,
      icon,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: className,
      },
    });
    if (eventName) {
      button.delegate('execute').to(this, eventName);
    }
    return button;
  }

  // CUSTOM
  _createT3LinkButton(editor, locale) {
    const button = new ButtonView(this.locale);
    button.set({
      label: 'TYPO3 Inhalt verlinken',
      icon: '<svg width="800px" height="800px" viewBox="-2 0 260 260" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" preserveAspectRatio="xMidYMid"><g><path d="M109.525333,4.05333333 C104.810667,8.08533333 101.461333,12.8 101.461333,26.88 C101.461333,65.1946667 149.824,180.288 182.762667,180.288 C186.462268,180.338187 190.147176,179.812799 193.685333,178.730667 L193.616,178.75 L192.774258,180.100166 C164.346546,225.411559 130.133077,258.650903 109.429541,259.317782 L108.8,259.328 C63.8293333,259.328 0,123.562667 0,63.8293333 C0,54.4213333 2.13333333,47.04 5.376,42.4533333 C20.8426667,23.552 69.2053333,8.74666667 109.525333,4.05333333 Z M172.672,0 C214.314667,0 256,6.72 256,30.2293333 C256,77.9306667 225.749333,135.744 210.304,135.744 C182.762667,135.744 148.437333,59.136 148.437333,20.8213333 C148.437333,3.34933333 155.136,0 172.608,0 L172.672,0 Z" fill="#F49700"></path></g></svg>',
      tooltip: true,
      class: 'ck-button-w100',
    });
    return button;
  }

  /**
   * Populates {@link module:ui/viewcollection~ViewCollection} of {@link module:ui/button/switchbuttonview~SwitchButtonView}
   * made based on {@link module:link/linkcommand~LinkCommand#manualDecorators}.
   *
   * @param linkCommand A reference to the link command.
   * @returns ViewCollection of switch buttons.
   */
  _createManualDecoratorSwitches(linkCommand) {
    const switches = this.createCollection();
    for (const manualDecorator of linkCommand.manualDecorators) {
      const switchButton = new SwitchButtonView(this.locale);
      switchButton.set({
        name: manualDecorator.id,
        label: manualDecorator.label,
        withText: true,
      });
      switchButton.bind('isOn').toMany([manualDecorator, linkCommand], 'value', (decoratorValue, commandValue) => {
        return commandValue === undefined && decoratorValue === undefined ? !!manualDecorator.defaultValue : !!decoratorValue;
      });
      switchButton.on('execute', () => {
        manualDecorator.set('value', !switchButton.isOn);
      });
      switches.add(switchButton);
    }
    return switches;
  }

  /**
   * Populates the {@link #children} collection of the form.
   *
   * If {@link module:link/linkcommand~LinkCommand#manualDecorators manual decorators} are configured in the editor, it creates an
   * additional `View` wrapping all {@link #_manualDecoratorSwitches} switch buttons corresponding
   * to these decorators.
   *
   * @param manualDecorators A reference to
   * the collection of manual decorators stored in the link command.
   * @returns The children of link form view.
   */
  _createFormChildren(manualDecorators) {
    const children = this.createCollection();
    children.add(this.urlInputView);
    children.add(this.linkT3PageButtonView);
    if (manualDecorators.length) {
      const additionalButtonsView = new View();
      additionalButtonsView.setTemplate({
        tag: 'ul',
        children: this._manualDecoratorSwitches.map(switchButton => ({
          tag: 'li',
          children: [switchButton],
          attributes: {
            class: [
              'ck',
              'ck-list__item',
            ],
          },
        })),
        attributes: {
          class: [
            'ck',
            'ck-reset',
            'ck-list',
          ],
        },
      });
      children.add(additionalButtonsView);
    }
    children.add(this.saveButtonView);
    children.add(this.cancelButtonView);
    return children;
  }

  /**
   * The native DOM `value` of the {@link #urlInputView} element.
   *
   * **Note**: Do not confuse it with the {@link module:ui/inputtext/inputtextview~InputTextView#value}
   * which works one way only and may not represent the actual state of the component in the DOM.
   */
  get url() {
    const {element} = this.urlInputView.fieldView;
    if (!element) {
      return null;
    }
    return element.value.trim();
  }
}
